'use client';;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();

import {
  OrganisationSwitchWarningDialog,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@workspace/ui';
import { useOrganisationSwitchConfirmation } from '@workspace/ui/hooks';
import { cn } from '@workspace/ui/lib';
import { ChevronsUpDown, LoaderCircle } from 'lucide-react';
import { useState } from 'react';
import { useCanShowMultiOrganisationSwitcher } from '@/hooks/use-can-show-multi-organisation-switcher';
import { useSwitchOrganisationItem } from '@/hooks/use-switch-organisation-item';
import { useUserVerification } from '@/hooks/use-user';
import { getActiveOrganisationFromUser } from '@/lib/active-organisation';
import {
  ORGANISATION_SWITCHER_SECTION_LABEL,
  buildOrganisationSwitcherItems,
  formatOrganisationSwitcherSubtitle,
  getOrganisationSwitchViewName,
  isOrganisationSwitcherItemActive,
} from '@/lib/organisation-switcher-items';
import { OrganisationSwitcherAvatar } from '@/components/layout/organisation-switcher-avatar';
import { useAuthStore } from '@/stores/authStore';

export function OrganisationSwitcher() {
  const user = useAuthStore((state) => state.user);

  const userId = user?.user.id;
  const activeOrganisationId = getActiveOrganisationFromUser(user)?.id;
  const canShowSwitcher = useCanShowMultiOrganisationSwitcher();
  const { switchToItem, isSwitching } = useSwitchOrganisationItem();
  const { pendingItem, requestSwitch, confirmSwitch, isDialogOpen, setDialogOpen } =
    useOrganisationSwitchConfirmation({ switchToItem });
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const { data: userVerification, isLoading } = useUserVerification(!!userId && canShowSwitcher);

  const organisations = userVerification?.organisations ?? [];
  const switcherItems = buildOrganisationSwitcherItems(organisations);
  const activeRoleContext = userVerification?.active_organisation?.role_context;
  const activeOrganisation = organisations.find(
    (organisation) => organisation.id === activeOrganisationId
  );

  const triggerSubtitle = formatOrganisationSwitcherSubtitle(
    activeRoleContext ?? '',
    activeOrganisation?.roles
  );

  if (!userId || !canShowSwitcher || switcherItems.length <= 1) return null;

  return (
    <>
      <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            translate="no"
            className={cn(
              'notranslate bg-grey-0 dark:bg-grey-1000 flex w-[240px] max-w-[240px] cursor-pointer items-center gap-2 rounded-full px-2 py-1.5',
              'hover:bg-grey-25 dark:hover:bg-grey-900 focus-visible:ring-primary focus-visible:ring-2 focus-visible:outline-none'
            )}
            disabled={isLoading || isSwitching}
          >
            <OrganisationSwitcherAvatar size="trigger" />
            <div className="flex min-w-0 flex-1 flex-col items-start gap-0.5">
              <p className="text-neutral-200 dark:text-neutral-100 w-full truncate text-left text-xs leading-none font-medium">
                {activeOrganisation?.name ?? 'Organisation'}
              </p>
              {triggerSubtitle ? (
                <p className="text-grey-700 dark:text-grey-400 w-full truncate text-left text-[10px] leading-3 font-normal">
                  {triggerSubtitle}
                </p>
              ) : null}
            </div>
            {isSwitching ? (
              <LoaderCircle className="text-neutral-200 dark:text-neutral-100 size-4 shrink-0 animate-spin" />
            ) : (
              <ChevronsUpDown className="text-grey-700 dark:text-grey-400 size-4 shrink-0" />
            )}
          </button>
        </PopoverTrigger>
        <PopoverContent
          side="bottom"
          align="end"
          sideOffset={12}
          className="bg-grey-0 dark:bg-grey-1000 notranslate flex w-[300px] flex-col gap-2 rounded-4xl p-4"
          translate="no"
        >
          <p className="text-grey-500 leading-4 text-[10px]">
            {ORGANISATION_SWITCHER_SECTION_LABEL}
          </p>
          <div className="flex flex-col gap-2">
            {switcherItems.map((item) => {
              const isActive = isOrganisationSwitcherItemActive(
                item,
                activeOrganisationId,
                activeRoleContext
              );
              return (
                <button
                  key={item.key}
                  type="button"
                  className={cn(
                    'text-neutral-200 cursor-pointer dark:text-neutral-100 hover:bg-grey-50 dark:hover:bg-grey-900 flex min-h-12 w-full items-center gap-3 rounded-xl px-3 py-2 text-left text-sm',
                    isActive && 'bg-grey-50 dark:bg-grey-900'
                  )}
                  disabled={isActive || isSwitching}
                  onClick={() => {
                    setIsPopoverOpen(false);
                    requestSwitch(item);
                  }}
                >
                  <OrganisationSwitcherAvatar size="md" />
                  <div className="flex min-w-0 flex-1 flex-col">
                    <p className="truncate font-medium leading-none text-sm">
                      {item.organisationName}
                    </p>
                    {item.subtitle ? (
                      <p className="text-grey-600 truncate leading-4 text-[10px]">
                        {item.subtitle}
                      </p>
                    ) : null}
                  </div>
                </button>
              );
            })}
          </div>
        </PopoverContent>
      </Popover>
      <OrganisationSwitchWarningDialog
        open={isDialogOpen}
        onOpenChange={setDialogOpen}
        onConfirm={confirmSwitch}
        viewName={pendingItem ? getOrganisationSwitchViewName(pendingItem) : ''}
        isConfirming={isSwitching}
      />
    </>
  );
}
